import ListItemButton from "@mui/material/ListItemButton"
import ListSubheader from "@mui/material/ListSubheader"
import Radio from "@mui/material/Radio"
import { useTheme } from '@mui/material/styles'
import Typography from "@mui/material/Typography"
import useMediaQuery from '@mui/material/useMediaQuery'
import { createContext, forwardRef, HTMLAttributes, ReactChild, useContext, useEffect, useRef } from "react"
import { ListChildComponentProps, VariableSizeList } from "react-window"

const LISTBOX_PADDING = 8 // px

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props
    const dataSet = data[index]
    const inlineStyle = {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING
    }

    if (dataSet.hasOwnProperty('group')) {
        return (
            <>
                {dataSet.group &&
                    <ListSubheader className="MuiListSubheader-sticky" key={dataSet.key} component="li" style={inlineStyle}>
                        <span style={{ color: '#ED0000', paddingLeft: "1rem" }}>{dataSet.group}</span>
                    </ListSubheader>
                }
            </>
        )
    }

    return (
        <>
            {dataSet[1].Id &&
                <ListItemButton component="li" key={dataSet[0].key} {...dataSet[0]} style={inlineStyle}>
                    <Radio color="secondary" checked={dataSet[2]} size={"small"} style={{ color: '#ED0000', paddingLeft: "1.5rem" }} />
                    <Typography variant="subtitle1" textAlign={"left"} marginBottom={0} data-vehicle-id={dataSet[1].Id}>{dataSet[0].key}</Typography>
                </ListItemButton>
            }
        </>
    )
}

const OuterElementContext = createContext({})

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = useContext(OuterElementContext)
    return <div ref={ref} {...props} {...outerProps} />
})

function useResetCache(data: any) {
    const ref = useRef<VariableSizeList>(null)
    useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true)
        }
    }, [data])
    return ref
}

// Adapter for react-window
const ListBox = forwardRef<
    HTMLDivElement,
    HTMLAttributes<HTMLElement>
>(function ListBox(props, ref) {
    const { children, ...other } = props
    const itemData: ReactChild[] = [];

    (children as ReactChild[]).forEach(
        (item: ReactChild & { children?: ReactChild[] }) => {
            itemData.push(item)
            itemData.push(...(item.children || []))
        },
    )

    const theme = useTheme()
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
        noSsr: true,
    })
    const itemCount = itemData.length
    const itemSize = smUp ? 36 : 48

    const getChildSize = (child: ReactChild) => {
        if (child.hasOwnProperty('group')) {
            return 48
        }

        return itemSize
    }

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
    }

    const gridRef = useResetCache(itemCount)

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    )
})

export default ListBox